'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { BarChart3, DollarSign, Users, Headphones, Download, ArrowUpRight, ArrowDownRight,  } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,  } from 'recharts';

const monthlyRevenue = [
  { month: 'Mar', revenue: 38200, expenses: 22000 },
  { month: 'Apr', revenue: 41500, expenses: 23500 },
  { month: 'May', revenue: 39800, expenses: 21800 },
  { month: 'Jun', revenue: 44200, expenses: 24000 },
  { month: 'Jul', revenue: 47600, expenses: 25200 },
  { month: 'Aug', revenue: 52100, expenses: 26800 },
];

const serviceRevenue = [
  { name: 'BPO Center', value: 19000, color: '#6366f1' },
  { name: 'Digital Marketing', value: 10780, color: '#22d3ee' },
  { name: 'VOIP / Calls', value: 8600, color: '#34d399' },
  { name: 'Web Development', value: 7200, color: '#fbbf24' },
  { name: 'IT Support', value: 4300, color: '#f87171' },
  { name: 'Other', value: 2220, color: '#94a3b8' },
];

const clientGrowth = [
  { month: 'Mar', clients: 14 },
  { month: 'Apr', clients: 15 },
  { month: 'May', clients: 15 },
  { month: 'Jun', clients: 17 },
  { month: 'Jul', clients: 18 },
  { month: 'Aug', clients: 20 },
];

const kpiData = [
  { label: 'Total Revenue (Aug)', value: '$52,100', change: '+9.5%', up: true, icon: DollarSign, color: 'text-success', bg: 'bg-success/10' },
  { label: 'Active Clients', value: '20', change: '+2 this month', up: true, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
  { label: 'BPO Calls Handled', value: '3,842', change: '+14.2%', up: true, icon: Headphones, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  { label: 'Avg Ticket Resolution', value: '18h', change: '-3h vs last month', up: true, icon: BarChart3, color: 'text-cyan-400', bg: 'bg-cyan-400/10' },
];

const servicePerformance = [
  { service: 'BPO Center', satisfaction: 93, utilization: 87, growth: 12 },
  { service: 'Digital Marketing', satisfaction: 91, utilization: 74, growth: 8 },
  { service: 'VOIP / Calls', satisfaction: 89, utilization: 68, growth: 5 },
  { service: 'Web Development', satisfaction: 95, utilization: 82, growth: 18 },
  { service: 'IT Support', satisfaction: 88, utilization: 61, growth: -2 },
];

const CHART_COLORS = { primary: '#6366f1', success: '#34d399', warning: '#fbbf24', muted: '#334155' };

export default function ReportsPage() {
  const [period, setPeriod] = useState<'6m' | '3m' | '1m'>('6m');

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Reports & Analytics</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Business performance, revenue trends, and service metrics</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex bg-card border border-border rounded-lg overflow-hidden">
              {(['1m', '3m', '6m'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-2.5 sm:px-3 py-1.5 text-xs font-medium transition-colors ${period === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button className="flex items-center gap-1.5 bg-card border border-border text-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-muted transition-colors">
              <Download size={15} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {kpiData.map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className={`text-xs mt-1 flex items-center gap-1 ${kpi.up ? 'text-success' : 'text-danger'}`}>
                {kpi.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {kpi.change}
              </p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Revenue vs Expenses */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Revenue vs Expenses</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyRevenue} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} opacity={0.3} />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#f1f5f9' }}
                  formatter={(v: number) => [`$${v.toLocaleString()}`, '']}
                />
                <Bar dataKey="revenue" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Revenue" />
                <Bar dataKey="expenses" fill={CHART_COLORS.muted} radius={[4, 4, 0, 0]} name="Expenses" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue by Service */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Revenue by Service</h2>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={serviceRevenue} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                  {serviceRevenue.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  formatter={(v: number) => [`$${v.toLocaleString()}`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-2">
              {serviceRevenue.slice(0, 4).map((s) => (
                <div key={s.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <span className="text-xs text-muted-foreground">{s.name}</span>
                  </div>
                  <span className="text-xs font-medium text-foreground">${(s.value / 1000).toFixed(1)}k</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Client Growth + Service Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Growth */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Client Growth</h2>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={clientGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.muted} opacity={0.3} />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} domain={[10, 25]} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#f1f5f9' }}
                />
                <Line type="monotone" dataKey="clients" stroke={CHART_COLORS.success} strokeWidth={2.5} dot={{ fill: CHART_COLORS.success, r: 4 }} name="Clients" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Service Performance */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold text-foreground mb-4">Service Performance</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left pb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</th>
                    <th className="text-left pb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Satisfaction</th>
                    <th className="text-left pb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Utilization</th>
                    <th className="text-right pb-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Growth</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {servicePerformance.map((sp) => (
                    <tr key={sp.service} className="hover:bg-muted/20 transition-colors">
                      <td className="py-3 font-medium text-foreground text-sm">{sp.service}</td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${sp.satisfaction}%` }} />
                          </div>
                          <span className="text-xs text-foreground">{sp.satisfaction}%</span>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-success rounded-full" style={{ width: `${sp.utilization}%` }} />
                          </div>
                          <span className="text-xs text-foreground">{sp.utilization}%</span>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <span className={`flex items-center justify-end gap-1 text-xs font-medium ${sp.growth >= 0 ? 'text-success' : 'text-danger'}`}>
                          {sp.growth >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {sp.growth >= 0 ? '+' : ''}{sp.growth}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
